import { useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toggleThemeReducer } from "../store/slices/miscSlice"; // adjust path

export const useTheme = () => {
  const dispatch = useDispatch();
  const appTheme = useSelector((state) => state.miscSlice.theme);

  /* Update <html> class when theme changes.
   *
   * useLayoutEffect, not useEffect: a theme flip changes styling through two channels at
   * once — components branching on `theme` in JS, and every CSS `dark:` utility, which keys
   * off this class. useEffect runs *after* the browser paints, so the JS half would land a
   * frame before the CSS half and that frame paints a half-converted page (light cards over
   * a dark background). The heavier the screen, the longer it lingers — which is why it read
   * as a lag on the outreach wizard but not on lighter screens. useLayoutEffect runs before
   * paint, so both halves land in the same frame and the switch is atomic.
   */
  useLayoutEffect(() => {
    const html = document.documentElement;

    /* Freeze transitions across the swap. Any element that both declares a background or
     * border which differs per theme and carries a transition on it — the outreach question
     * cards, for instance — otherwise *animates* from the old theme's colour to the new one.
     * The rest of the page changes instantly, so those elements smear as light cards over an
     * already-dark page for the length of their transition, which is the lag that shows up on
     * card-heavy screens. Elements that only theme their hover state have nothing to tween and
     * were never affected, which is why this looked specific to certain screens.
     */
    html.classList.add("theme-switching");

    if (appTheme === "dark") {
      html.classList.add("dark");
      html.classList.remove("light");
    } else {
      html.classList.remove("dark");
      html.classList.add("light");
    }

    // Released two frames on: the first fires before the new colours have painted, so
    // unfreezing there would simply let the tween start after all.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => html.classList.remove("theme-switching"));
    });
    // rAF is paused in a background tab, so toggling and switching away could otherwise
    // leave the freeze in place until the tab is looked at again. Nothing is painting
    // meanwhile so it changes nothing visually, but a stuck app-wide `!important` rule is a
    // miserable thing to track down later, so it always gets torn down on a timer too.
    const fallback = setTimeout(() => html.classList.remove("theme-switching"), 300);
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      clearTimeout(fallback);
      html.classList.remove("theme-switching");
    };
  }, [appTheme]);

  // Function to toggle theme
  const toggleTheme = () => {
    dispatch(toggleThemeReducer());
  };

  return { theme: appTheme, toggleTheme };
};
